import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Icons } from '@/assets';

export interface ConfirmDialogAction {
  id: string;
  label: string;
  variant?: 'primary' | 'secondary' | 'danger';
}

interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDangerous?: boolean;
  actions?: ConfirmDialogAction[];
  tone?: 'success' | 'danger' | 'info';
}

type ConfirmCallback = (confirmed: boolean) => void;
type ActionCallback = (actionId: string | null) => void;

const ConfirmDialogContext = React.createContext<{
  show: (options: ConfirmDialogOptions) => Promise<boolean>;
  showActions: (options: ConfirmDialogOptions) => Promise<string | null>;
} | null>(null);

export const useConfirmDialog = () => {
  const context = React.useContext(ConfirmDialogContext);
  if (!context) {
    throw new Error('useConfirmDialog must be used within ConfirmDialogProvider');
  }
  return context;
};

export const ConfirmDialogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmDialogOptions>({
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    isDangerous: false,
    actions: [],
    tone: 'success',
  });
  const [callback, setCallback] = useState<ConfirmCallback | ActionCallback | null>(null);
  const [mode, setMode] = useState<'boolean' | 'actions'>('boolean');

  const show = useCallback(
    (dialogOptions: ConfirmDialogOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        setMode('boolean');
        setOptions({
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          isDangerous: false,
          actions: [],
          tone: dialogOptions.isDangerous ? 'danger' : 'success',
          ...dialogOptions,
        });
        setCallback(() => (confirmed: boolean) => {
          resolve(confirmed);
          setIsOpen(false);
        });
        setIsOpen(true);
      });
    },
    []
  );

  const showActions = useCallback(
    (dialogOptions: ConfirmDialogOptions): Promise<string | null> => {
      return new Promise((resolve) => {
        setMode('actions');
        setOptions({
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          isDangerous: false,
          actions: [],
          tone: dialogOptions.isDangerous ? 'danger' : 'success',
          ...dialogOptions,
        });
        setCallback(() => (actionId: string | null) => {
          resolve(actionId);
          setIsOpen(false);
        });
        setIsOpen(true);
      });
    },
    []
  );

  const handleConfirm = () => {
    if (callback) {
      if (mode === 'actions') {
        (callback as ActionCallback)('confirm');
      } else {
        (callback as ConfirmCallback)(true);
      }
    }
  };

  const handleCancel = () => {
    if (callback) {
      if (mode === 'actions') {
        (callback as ActionCallback)('cancel');
      } else {
        (callback as ConfirmCallback)(false);
      }
    }
  };

  const handleAction = (actionId: string | null) => {
    if (!callback) return;
    if (mode === 'actions') {
      (callback as ActionCallback)(actionId);
    } else {
      (callback as ConfirmCallback)(actionId === 'confirm');
    }
  };

  const resolvedTone = options.tone ?? (options.isDangerous ? 'danger' : 'success');
  const iconContainerClass = resolvedTone === 'danger'
    ? 'bg-red-50 text-red-600'
    : resolvedTone === 'info'
      ? 'bg-blue-50 text-[#0a68ff]'
      : 'bg-green-30 text-green-500';
  const confirmButtonClass = options.isDangerous
    ? 'bg-red-600 text-white hover:bg-red-700'
    : 'bg-[#0a68ff] text-white hover:bg-[#0854cc]';
  const renderedActions = mode === 'actions'
    ? (options.actions || [])
    : [
        { id: 'cancel', label: options.cancelText || 'Cancel', variant: 'secondary' as const },
        {
          id: 'confirm',
          label: options.confirmText || 'Confirm',
          variant: options.isDangerous ? 'danger' as const : 'primary' as const,
        },
      ];
  const actionsContainerClassName =
    renderedActions.length <= 2
      ? `grid w-full gap-3 ${
          renderedActions.length === 1 ? 'grid-cols-1' : 'grid-cols-1 sm:grid-cols-2'
        }`
      : 'flex w-full flex-row gap-3 justify-center';

  const getActionClassName = (action: ConfirmDialogAction) => {
    if (action.variant === 'danger') {
      return 'bg-red-600 text-white hover:bg-red-700';
    }
    if (action.variant === 'secondary') {
      return 'text-gray-90 bg-white border border-gray-30 hover:bg-gray-10';
    }
    return 'bg-[#0a68ff] text-white hover:bg-[#0854cc]';
  };

  const renderIcon = () => {
    if (resolvedTone === 'danger') {
      return <Icons.ErrorIcon className="w-8 h-8" />;
    }
    if (resolvedTone === 'info') {
      return <Icons.InfoIcon className="w-8 h-8" />;
    }
    return <Icons.TickIcon className="w-8 h-8" />;
  };

  return (
    <ConfirmDialogContext.Provider value={{ show, showActions }}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {isOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={handleCancel}
                  className="fixed inset-0 bg-black/50 z-[300]"
                />

                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 flex items-center justify-center z-[310] p-4"
                  onClick={(e) => {
                    if (e.target === e.currentTarget) {
                      handleCancel();
                    }
                  }}
                >
                  <div
                    className={`bg-white rounded-lg shadow-lg w-full overflow-hidden border border-gray-20 ${
                      renderedActions.length >= 3 ? 'max-w-[560px]' : 'max-w-[400px]'
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center px-6 py-6 text-center">
                      <div
                        className={`w-16 h-16 rounded-full flex items-center justify-center mb-5 animate-in zoom-in duration-300 ${iconContainerClass}`}
                      >
                        {renderIcon()}
                      </div>

                      <h2 className="text-lg font-medium text-gray-110 mb-2">
                        {options.title}
                      </h2>

                      <p className="text-gray-500 mb-8 leading-relaxed">
                        {options.message}
                      </p>

                      <div className={actionsContainerClassName}>
                        {renderedActions.map((action) => (
                          <button
                            key={action.id}
                            onClick={() => {
                              if (action.id === 'cancel' && mode === 'boolean') {
                                handleCancel();
                                return;
                              }
                              if (action.id === 'confirm' && mode === 'boolean') {
                                handleConfirm();
                                return;
                              }
                              handleAction(action.id);
                            }}
                            className={`h-[52px] rounded-full px-6 text-sm font-medium whitespace-nowrap transition-colors ${
                              renderedActions.length <= 2 ? 'w-full' : 'flex-1 min-w-0'
                            } ${getActionClassName(action)}`}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>,
          document.body
        )}
    </ConfirmDialogContext.Provider>
  );
};

export default ConfirmDialogProvider;
